'use client';

import { useEffect, useMemo, useState } from 'react';
import { useParams, useRouter } from 'next/navigation';
import { useTranslation } from '@/hooks/useTranslation';
import { useAuthGuard } from '@/hooks/useAuthGuard';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, ArrowLeft } from 'lucide-react';
import { knowledgeBaseApi, type KnowledgeArticle } from '../../_lib/api';
import { DocumentEditor } from '@/features/document-editor';

const statusVariantMap: Record<KnowledgeArticle['status'], 'default' | 'secondary' | 'destructive' | 'outline'> = {
  DRAFT: 'secondary',
  PUBLISHED: 'default',
  ARCHIVED: 'outline',
};

export default function KnowledgeBaseArticleDetailPage() {
  const { t } = useTranslation();
  const { isReady } = useAuthGuard();
  const router = useRouter();
  const params = useParams();
  const articleId = useMemo(() => {
    const raw = params?.id;
    if (Array.isArray(raw)) {
      return raw[0] || '';
    }
    return raw ?? '';
  }, [params]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [article, setArticle] = useState<KnowledgeArticle | null>(null);

  useEffect(() => {
    if (!isReady) return;
    if (!articleId) {
      setError(t.knowledgeBase.articleDetail.loadFailed);
      setLoading(false);
      return;
    }

    const loadArticle = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await knowledgeBaseApi.getArticle(articleId);
        setArticle(response);
      } catch (err) {
        console.error('Failed to load article:', err);
        setError(t.knowledgeBase.articleDetail.loadFailed);
        setArticle(null);
      } finally {
        setLoading(false);
      }
    };

    loadArticle();
  }, [articleId, isReady, t]);

  if (!isReady) {
    return (
      <div className="flex h-full items-center justify-center bg-[#f4f5f7]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col bg-[#f4f5f7]">
      <div className="flex-shrink-0 border-b border-gray-200 bg-white px-6 py-2">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            className="gap-1.5"
            onClick={() => router.push('/knowledge-base')}
          >
            <ArrowLeft className="h-4 w-4" />
            {t.knowledgeBase.articleDetail.back}
          </Button>
          <div>
            <h1 className="text-lg font-semibold text-gray-900">
              {t.knowledgeBase.articleDetail.title}
            </h1>
            <p className="text-sm text-gray-500">
              {t.knowledgeBase.articleDetail.subtitle}
            </p>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-6">
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t.knowledgeBase.articleDetail.loading}
            </div>
          )}

          {!loading && error && (
            <div className="text-sm text-red-600">
              {error}
            </div>
          )}

          {!loading && article && (
            <>
              <div className="mb-4">
                <h2 className="text-xl font-semibold text-gray-900">{article.title}</h2>
                <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    {t.knowledgeBase.articleDetail.fields.status}
                    <Badge variant={statusVariantMap[article.status]}>
                      {t.knowledgeBase.articleDetail.status[article.status]}
                    </Badge>
                  </span>
                  <span>
                    {t.knowledgeBase.articleDetail.fields.lastUpdated}: {new Date(article.updatedAt).toLocaleString()}
                  </span>
                </div>
              </div>
              <DocumentEditor
                value={article.content}
                readOnly
                showToolbar={false}
                showWordCount={false}
                minHeight="400px"
                className="border border-gray-100 rounded-lg"
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
